import { compact } from "lodash";
import { useMemo, useState } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupListFragmentFragment,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  Maybe,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import { Select } from "@components/select/select";
import { assessmentGroupAnonymityOptions } from "@helpers/constants";
import { assertNonNull } from "@helpers/helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import { ComplianceProgramStep } from "../compliance-program-form-steps";
import { AssessmentTemplateExtraSettings } from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import QuestionSetSelector from "./question-set-selector";

const EngagementSurveyStep = ({
  proposedComplianceProgram,
  canUpdate,
  hideSettings,
  complianceProgramSteps,
  onUpdateComplianceProgramSteps,
  onUpdateAssessmentTemplateSettings,
  onUpdateAssessmentTemplate,
  availableAssessmentGroups,
  onRemoveAssessmentTemplate,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  proposedComplianceProgram: SaveComplianceProgramMutationVariables;
  canUpdate: boolean;
  hideSettings?: boolean;
  complianceProgramSteps: ComplianceProgramStep[];
  onUpdateComplianceProgramSteps: (steps: ComplianceProgramStep[]) => void;
  onUpdateAssessmentTemplateSettings: (
    assessmentType: AssessmentType
  ) => (settings: AssessmentTemplateExtraSettings) => void;
  onUpdateAssessmentTemplate: (
    assessmentType: AssessmentType
  ) => (opt: ComboboxGenericOption<number> | null) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onRemoveAssessmentTemplate: (assessmentType: AssessmentType) => void;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment
  ) => void;
  onAssessmentGroupArchived: (
    assessmentType: AssessmentType,
    assessmentGroupId: number
  ) => void;
}) => {
  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(
        ({ assessmentType }) =>
          assessmentType === AssessmentType.EngagementSurvey
      )
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const selectedAssessmentTemplates = useMemo(
    () =>
      proposedComplianceProgram.assessmentTemplates
        ? compact(
            proposedComplianceProgram.assessmentTemplates as Maybe<AssessmentTemplateComplianceRequirement>[]
          )
        : [],
    [proposedComplianceProgram.assessmentTemplates]
  );

  const selectedEngagementSurveyAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.EngagementSurvey
    );
  }, [selectedAssessmentTemplates]);

  const selectedAssessmentGroup = useMemo(() => {
    return selectedEngagementSurveyAssessmentTemplate &&
      selectedEngagementSurveyAssessmentTemplate.questionSetIds.length > 0
      ? availableAssessmentGroups.find(
          ({ id }) =>
            id === selectedEngagementSurveyAssessmentTemplate.questionSetIds[0]
        ) ?? null
      : null;
  }, [availableAssessmentGroups, selectedEngagementSurveyAssessmentTemplate]);

  return (
    <>
      {isAssessmentGroupSidebarOpen && (
        <AssessmentGroupSidebar
          assessmentGroupId={selectedAssessmentGroup?.id}
          assessmentType={AssessmentType.EngagementSurvey}
          assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
          onClose={() => {
            setAssessmentGroupToDuplicate(null);
            setIsAssessmentGroupSidebarOpen(false);
          }}
          onAssessmentGroupCreated={(assessmentGroup) => {
            onAssessmentGroupCreated(assessmentGroup);
            onUpdateAssessmentTemplate(AssessmentType.EngagementSurvey)({
              value: assessmentGroup.id,
              label: assessmentGroup.title,
            });
          }}
          onAssessmentGroupArchived={(assessmentGroupId) =>
            onAssessmentGroupArchived(
              AssessmentType.EngagementSurvey,
              assessmentGroupId
            )
          }
        />
      )}
      <ComplianceProgramFormStep
        canDelete={false}
        title="Survey"
        onDelete={() => {
          onRemoveAssessmentTemplate(AssessmentType.EngagementSurvey);
          onUpdateComplianceProgramSteps(
            complianceProgramSteps.filter(
              (s) => s !== ComplianceProgramStep.ENGAGEMENT_SURVEY
            )
          );
        }}
      >
        <div className="p-6 flex flex-col gap-3">
          <div className="text-sm text-gray-600 font-medium mb-4">
            Sections (For Form Input)
          </div>
          <div className="flex items-center gap-3 p-4 bg-white border rounded">
            <QuestionSetSelector
              assessmentType={AssessmentType.EngagementSurvey}
              availableAssessmentGroupSelectOptions={
                availableAssessmentGroupSelectOptions
              }
              canUpdate={canUpdate}
              onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
              onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
              onOpenAssessmentGroupSidebar={() =>
                setIsAssessmentGroupSidebarOpen(true)
              }
              selectedAssessmentGroup={selectedAssessmentGroup}
            />
          </div>
        </div>

        {!hideSettings && (
          <div className="p-6 border-t">
            <div className="text-sm text-gray-600 font-medium mb-4">
              Settings
            </div>
            <FormFields
              fields={[
                {
                  title: "Anonymity",
                  render: () =>
                    selectedEngagementSurveyAssessmentTemplate ? (
                      <Select<AssessmentGroupAnonymity>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedEngagementSurveyAssessmentTemplate.anonymity
                        )}
                        options={assessmentGroupAnonymityOptions(
                          AssessmentType.EngagementSurvey
                        )}
                        onChange={(opt) =>
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.EngagementSurvey
                          )({
                            anonymity: opt.value,
                          })
                        }
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
              ]}
            />
          </div>
        )}
      </ComplianceProgramFormStep>
    </>
  );
};
export default EngagementSurveyStep;
